import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { PbsIcon } from "../common/icons";
import { LayoutGrid } from "lucide-react";
import { ClipboardList } from "lucide-react";
import "./Widgets.css";
import { useWidgets } from "@/context/WidgetContext";

export interface IWidget {
  icon: string;
  title: string;
  content?: React.ReactNode;
  size: "small" | "medium" | "large" | "full";
}

const getSheetWidth = (size?: string) => {
  switch (size) {
    case 'small': return 'w-full sm:max-w-[600px]';
    case 'medium': return 'w-full sm:max-w-[900px]';
    case 'large': return 'w-full sm:max-w-[1200px]';
    case 'full': return 'w-full';
    default: return 'w-full sm:max-w-[900px]';
  }
};

const WidgetBar = () => {
  const { widgets, openWidget, setOpenWidget } = useWidgets();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleWidgetClick = (widget: IWidget) => setOpenWidget(widget);
  const handlePanelDismiss = () => setOpenWidget(null);

  const sheetPanel = (
    <Sheet open={!!openWidget} onOpenChange={(open) => !open && handlePanelDismiss()}>
      <SheetContent
        side="right"
        className={getSheetWidth(openWidget?.size)}
        style={{ top: '70px', height: 'calc(100vh - 70px)', right: '0', overflow: 'scroll' }}
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {openWidget?.title === 'Service Request' && <ClipboardList className="h-5 w-5 text-primary" />}
            {openWidget?.title}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 overflow-auto flex-1">
          {openWidget?.content}
        </div>
      </SheetContent>
    </Sheet>
  );

  // Mobile view - bottom right launcher
  if (isMobile) {
    return (
      <>
        <div className="widget-launcher-mobile">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="rounded-full h-12 w-12 min-w-0 shadow-lg"
                title="Open widgets menu"
                aria-label="Open widgets menu"
              >
                <LayoutGrid className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {widgets.map((widget) => (
                <DropdownMenuItem key={widget.title} onClick={() => handleWidgetClick(widget)}>
                  <PbsIcon iconName={widget.icon} size={16} className="mr-2" />
                  {widget.title}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {sheetPanel}
      </>
    );
  }

  // Desktop view - sheet only (trigger is in the header)
  return <>{sheetPanel}</>;
};

export default WidgetBar;
